// frontend/src/pages/model/calcLogic.js
// Phase 3 step 13c-3: the rules behind the Calculations section and the formula
// editor, pure and unit-tested (no React).
//
// The server (app/semantic/calculations*.py) parses, checks and compiles every
// formula; it is the only authority. These rules shape the editor's draft, mirror
// the server's name rule so the editor can say so before a round trip, turn the
// server's typed errors into the field they belong to, and build the text that
// clicking a field or a function inserts into the formula.
import { TYPE_LABELS } from './modelLogic';

export const KINDS = ['column', 'measure'];
export const KIND_LABELS = { column: 'Calculated column', measure: 'Measure' };
export const KIND_HELP = {
  column: 'One value per row, from the row’s fields. Use it like any field: group by it, filter on it, or sum it.',
  measure: 'One value per group, from totals. Every field goes inside an aggregate such as sum() or count().',
};
export const KIND_EXAMPLES = {
  column: ['salary / 12', "if(level = 'Senior', salary, 0)", 'lower(trim(email))', 'days_between(closed_at, opened_at)'],
  measure: ['sum(salary) / count_distinct(remote_id)', "countif(status = 'open') / count()", 'avg(salary)'],
};

export const MAX_NAME = 50;            // mirrors MAX_NAME in app/semantic/calculations.py
export const MAX_DISPLAY_NAME = 255;
export const MAX_DESCRIPTION = 2000;
export const MAX_SOURCE = 2000;        // mirrors MAX_SOURCE in app/semantic/expr_parser.py
export const NAME_RE = /^[a-z][a-z0-9_]*$/;
export const FORMAT_LABELS = { number: 'Number', integer: 'Whole number', percent: 'Percent', currency: 'Currency' };
export const CHECK_DELAY_MS = 350;     // how long after the last keystroke the live check runs
export const KEYWORDS = ['and', 'or', 'not', 'true', 'false', 'null', 'case', 'when', 'then', 'else', 'end'];
export const CALC_KEY = /^_(calc|metric)_[a-z][a-z0-9_]{0,49}$/;

// --- the draft ---------------------------------------------------------------

/** A new formula of this kind: nothing typed yet. */
export function emptyCalc(kind) {
  return { kind: KINDS.includes(kind) ? kind : 'column', name: '', display_name: '', source: '', description: '', format: null, null_policy: 'null' };
}

/** The editor's draft for an existing calculation (from the listing). */
export function draftFrom(calc) {
  return {
    kind: calc.kind, name: calc.name, display_name: calc.display_name || '', source: calc.source || '',
    description: calc.description || '', format: calc.format || null, null_policy: calc.null_policy === 'zero' ? 'zero' : 'null',
  };
}

/** "Monthly pay" -> "monthly_pay": the name the editor suggests from a display name (the person can change it). */
export function suggestName(displayName) {
  let name = String(displayName || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (/^[0-9]/.test(name)) name = `c_${name}`;
  return name.slice(0, MAX_NAME).replace(/_+$/g, '');
}

/** Why a name would be refused by the server, or null. `reserved` is the set of function names (from the listing) plus the keywords. */
export function nameProblem(name, reserved = new Set()) {
  const n = String(name || '');
  if (!n) return 'Give it a name: how formulas refer to it.';
  if (n.length > MAX_NAME) return `A name is at most ${MAX_NAME} characters.`;
  if (!NAME_RE.test(n)) return 'Lowercase letters, digits and _ only, starting with a letter.';
  if (KEYWORDS.includes(n) || reserved.has(n)) return `${n} is a word formulas use; pick another name.`;
  return null;
}

/** The function names and keywords a name may not take. */
export function reservedNames(functions) {
  return new Set([...KEYWORDS, ...(functions || []).map((f) => f.name)]);
}

export function sourceProblem(source) {
  const s = String(source || '');
  if (!s.trim()) return 'Type a formula.';
  if (s.length > MAX_SOURCE) return `A formula is at most ${MAX_SOURCE} characters.`;
  return null;
}

/** What is wrong with the draft before it is sent, by field; {} when nothing is. */
export function draftProblems(draft, reserved = new Set(), { isNew = true } = {}) {
  const out = {};
  if (isNew) {
    const n = nameProblem(draft.name, reserved);
    if (n) out.name = n;
  }
  const s = sourceProblem(draft.source);
  if (s) out.source = s;
  if (String(draft.display_name || '').length > MAX_DISPLAY_NAME) out.display_name = `At most ${MAX_DISPLAY_NAME} characters.`;
  if (String(draft.description || '').length > MAX_DESCRIPTION) out.description = `At most ${MAX_DESCRIPTION} characters.`;
  return out;
}

const clean = (text) => { const t = String(text == null ? '' : text).trim(); return t === '' ? null : t; };

/** The body of POST .../calculations for a new formula. */
export function toCreateBody(draft) {
  const body = { kind: draft.kind, name: String(draft.name || '').trim(), source: String(draft.source || ''), null_policy: draft.null_policy === 'zero' ? 'zero' : 'null' };
  const display = clean(draft.display_name);
  const description = clean(draft.description);
  if (display) body.display_name = display;
  if (description) body.description = description;
  if (draft.format) body.format = draft.format;
  return body;
}

/** The body of PATCH .../calculations/{key}: only what changed against `current` (the listing's item); null when nothing did. */
export function toPatchBody(draft, current) {
  const patch = {};
  const display = clean(draft.display_name) || '';
  if (display !== String(current.display_name || '')) patch.display_name = display || null;
  if ((clean(draft.description) || null) !== (current.description || null)) patch.description = clean(draft.description);
  if (String(draft.source || '') !== String(current.source || '')) patch.source = String(draft.source || '');
  if ((draft.format || null) !== (current.format || null)) patch.format = draft.format || null;
  const policy = draft.null_policy === 'zero' ? 'zero' : 'null';
  if (policy !== (current.null_policy || 'null')) patch.null_policy = policy;
  return Object.keys(patch).length ? patch : null;
}

/** The body of POST .../calculations/check for the draft; `key` is the calculation being edited, if any. */
export function toCheckBody(draft, key = null) {
  const body = { kind: draft.kind, source: String(draft.source || '') };
  const name = String(draft.name || '').trim();
  if (name && !key) body.name = name;      // a new formula: the name is checked too; an edit keeps its name
  if (key) body.key = key;
  return body;
}

// --- errors --------------------------------------------------------------------

/** Which control a server error (SemanticError) belongs to: name, display_name, source, format, or form for the rest. */
export function errorTarget(err) {
  const path = err && typeof err.path === 'string' ? err.path : '';
  const m = /^\$\.([a-z_]+)/.exec(path);
  const field = m ? m[1] : '';
  return ['name', 'display_name', 'source', 'format', 'description', 'null_policy'].includes(field) ? field : 'form';
}

/** The formula split around the error's [start, end): { before, bad, after }. A bad or empty range marks the end. */
export function errorRange(source, pos) {
  const s = String(source || '');
  let [a, b] = Array.isArray(pos) && pos.length === 2 ? pos : [s.length, s.length];
  a = Math.max(0, Math.min(Number(a) || 0, s.length));
  b = Math.max(a, Math.min(Number(b) || a, s.length));
  if (a === b && a < s.length) b = a + 1;          // a point: mark one character
  return { before: s.slice(0, a), bad: s.slice(a, b), after: s.slice(b) };
}

// --- inserting into the formula ----------------------------------------------

const PLAIN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** How a formula refers to this field or calculation: its label, in brackets when it needs them. */
export function referenceText(item) {
  const name = String((item && (item.display_name || item.name || item.key)) || '');
  return PLAIN.test(name) ? name : `[${name.replace(/\]/g, '')}]`;
}

/** The text a function inserts and where the caret goes: inside the brackets unless it takes nothing. */
export function functionInsert(fn) {
  const text = `${fn.name}()`;
  const noArgs = /\(\s*\)\s*$/.test(String(fn.signature || ''));
  return { text, caret: noArgs ? text.length : text.length - 1 };
}

/** `text` put into `source` over the selection [start, end); returns the new source and caret. */
export function insertAt(source, start, end, text, caretOffset = text.length) {
  const s = String(source || '');
  const a = Math.max(0, Math.min(Number(start) || 0, s.length));
  const b = Math.max(a, Math.min(Number(end) || a, s.length));
  const pad = a > 0 && !/\s|\(|\[$/.test(s[a - 1]) && !/^\)|,/.test(text) ? ' ' : '';
  const next = `${s.slice(0, a)}${pad}${text}${s.slice(b)}`;
  return { source: next, caret: a + pad.length + caretOffset };
}

// --- the reference panel -------------------------------------------------------

const FUNCTION_GROUPS = [
  ['Text', ['lower', 'upper', 'trim', 'length', 'left', 'right', 'replace', 'contains', 'starts_with', 'concat', 'to_text', 'to_number']],
  ['Numbers', ['abs', 'round', 'floor', 'ceil']],
  ['Logic', ['coalesce', 'is_null', 'if']],
  ['Dates', ['year', 'quarter', 'month', 'day', 'weekday', 'days_between', 'date_add', 'today', 'now']],
];

/** The function reference in groups: Text, Numbers, Logic, Dates, then Aggregates (for measures); a search narrows it. */
export function groupFunctions(functions, query = '', { kind = 'column' } = {}) {
  const q = String(query || '').trim().toLowerCase();
  const list = (functions || []).filter((f) => !q || f.name.includes(q) || String(f.help || '').toLowerCase().includes(q));
  const byName = Object.fromEntries(list.map((f) => [f.name, f]));
  const groups = FUNCTION_GROUPS.map(([label, names]) => ({ label, items: names.map((n) => byName[n]).filter(Boolean) }));
  const placed = new Set(FUNCTION_GROUPS.flatMap(([, names]) => names));
  const aggregates = list.filter((f) => f.aggregate);
  const other = list.filter((f) => !f.aggregate && !placed.has(f.name));
  if (other.length) groups.push({ label: 'Other', items: other });
  if (kind === 'measure') groups.unshift({ label: 'Aggregates', items: aggregates });
  else groups.push({ label: 'Aggregates (measures only)', items: aggregates });
  return groups.filter((g) => g.items.length > 0);
}

/** The fields a formula may use, in label order: visible, chartable; a search narrows them. */
export function usableFields(fields, query = '') {
  const q = String(query || '').trim().toLowerCase();
  return (fields || [])
    .filter((f) => !f.hidden && f.role !== 'ignore' && !f.structured)
    .filter((f) => !q || String(f.display_name || '').toLowerCase().includes(q) || String(f.key).toLowerCase().includes(q))
    .sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
}

/** The other calculations a formula of this kind may use: columns always; measures only inside a measure. Never itself. */
export function usableCalculations(calculations, kind, selfKey = null, query = '') {
  const q = String(query || '').trim().toLowerCase();
  return (calculations || [])
    .filter((c) => c.is_enabled && !c.error && c.key !== selfKey && (kind === 'measure' || c.kind === 'column'))
    .filter((c) => !q || String(c.display_name || '').toLowerCase().includes(q) || String(c.name).toLowerCase().includes(q))
    .sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
}

// --- the list ------------------------------------------------------------------

export const typeText = (dataType) => TYPE_LABELS[dataType] || String(dataType || '');

/** What the Result column says: the type, or why it does not run. */
export function statusOf(calc) {
  if (!calc.is_enabled) return { tone: 'off', text: 'Off' };
  if (calc.error) return { tone: 'bad', text: 'Broken' };
  return { tone: 'ok', text: calc.kind === 'measure' ? 'Number' : typeText(calc.data_type) };
}

export function usageText(calc) {
  const charts = Array.isArray(calc.used_by) ? calc.used_by.length : 0;
  const formulas = Array.isArray(calc.used_by_calculations) ? calc.used_by_calculations.length : 0;
  const parts = [];
  if (charts) parts.push(charts === 1 ? '1 chart' : `${charts} charts`);
  if (formulas) parts.push(formulas === 1 ? '1 formula' : `${formulas} formulas`);
  return parts.length ? parts.join(', ') : 'Not used';
}

/** "Payroll is used by 2 charts and 1 formula": the in-use dialog's title for a formula or a field. */
export function inUseTitleFor(name, charts, calculations) {
  const parts = [];
  if (charts) parts.push(charts === 1 ? '1 chart' : `${charts} charts`);
  if (calculations) parts.push(calculations === 1 ? '1 formula' : `${calculations} formulas`);
  return `${name} is used by ${parts.join(' and ') || 'nothing'}`;
}

/** The sentence under the title when a change to a formula would break things. */
export function consequenceText(action) {
  const what = action === 'delete' ? 'deleting it' : action === 'disable' ? 'turning it off' : 'this change';
  return `If you go ahead, ${what} leaves these without a value, and they will show an error until someone edits them.`;
}

export const sortCalculations = (list) => [...(list || [])].sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));

export const replaceCalc = (list, updated) => (list.some((c) => c.key === updated.key)
  ? list.map((c) => (c.key === updated.key ? updated : c))
  : [...list, updated]);
